import { FC } from 'react';
import { List, Skeleton } from 'antd';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { TbForms, TbUserCircle } from 'react-icons/tb';
import { IoFolderOpen } from 'react-icons/io5';
import { IoIosArrowForward } from 'react-icons/io';
import Box from 'src/components/Box';
import StatusTag from 'src/components/StatusTag';
import FormDrawer from 'src/components/FormDrawer';
import { AnswerResponse } from 'src/types';
import { FormBuilderData, FormBuilderFormData } from 'src/types/formBuilder';

export function convertFormModelToFormBuilderForm(model: any): FormBuilderData {
  return {
    id: model.uuid,
    name: model.title,
    imageUrl: '',
    description: model.description ?? '',
    form: model.questions.map((item: any): FormBuilderFormData => {
      const newItem: FormBuilderFormData = {
        label: item.label,
        name: item.id,
        required: item.required,
        type: (item.type as string).toLowerCase() as any,
        answer: item.answer,
      };

      if (item.type === 'Short') {
        newItem.type = 'input';
        return newItem;
      }

      if (item.type === 'Number') {
        return {
          ...newItem,
          type: 'input',
          inputType: 'number',
        };
      }

      if (item.type === 'Paragraph') {
        newItem.type = 'textArea';
        return newItem;
      }

      if (item.type === 'Dropdown') {
        return {
          ...newItem,
          type: 'select',
          mode: 'simple',
          options: model.questions
            .filter(
              (subItem: any) =>
                subItem.type === 'DropdownOption' && subItem.parent === item.id
            )
            .map((subItem: any) => ({
              label: subItem.label,
              value: subItem.label,
            })),
        };
      }

      if (item.type === 'Checkbox') {
        return {
          ...newItem,
          type: 'checkbox',
          responsive: model.responsive,
          mode: 'simple',
          options: model.questions
            .filter((subItem: any) => subItem.parent === item.id)
            .map((subItem: any) => ({
              label: subItem.label,
              value: subItem.label,
            })),
        };
      }

      return newItem;
    }),
  };
}

interface FormsProps {
  className?: string;
  boxClassName?: string;
  data?: AnswerResponse[];
  loading?: boolean;
}

const Forms: FC<FormsProps> = ({ className, boxClassName, data, loading }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [search] = useSearchParams();

  const formId = search.get('form_id');

  return (
    <>
      {formId && <FormDrawer id={formId} />}

      <div className={`flex w-full flex-col gap-2 ${className}`}>
        <div className="flex items-center gap-2 text-blue-600">
          <TbForms className="text-2xl" />

          <span className="font-bold">Forms</span>
        </div>

        <Box size="default" sizeType="relative" className={boxClassName}>
          {loading ? (
            <Skeleton className="py-4" />
          ) : (
            <List
              dataSource={data}
              renderItem={(item: any) => (
                <List.Item
                  className="cursor-pointer"
                  onClick={() =>
                    navigate(`${location.pathname}?form_id=${item?.uuid}`, {
                      preventScrollReset: true,
                    })
                  }
                  key={item?.uuid}
                >
                  <div className="flex w-full items-center justify-between">
                    <div>
                      <div>{item?.title}</div>
                      <div className="flex flex-wrap">
                        <div className="flex items-center gap-[4px] text-blue-500">
                          <TbUserCircle className="text-[18px]" />
                          <span>{item?.project?.customer?.business_name}</span>
                        </div>
                        &nbsp;/&nbsp;{' '}
                        <div className="flex items-center gap-[4px] text-blue-500">
                          <IoFolderOpen />
                          <span>{item?.project?.title}</span>
                        </div>
                      </div>
                    </div>

                    <div className="whitespace-nowrap">
                      <StatusTag value={item?.status} />
                      <IoIosArrowForward className="ml-1 inline-block text-lg text-gray-600" />
                    </div>
                  </div>
                </List.Item>
              )}
            />
          )}
        </Box>
      </div>
    </>
  );
};

export default Forms;
